// Export a Video Studio project to MP4 by rendering every frame to canvas,
// then assembling video + audio with ffmpeg.wasm (avoids MediaRecorder bugs
// where only the last scene is captured).
//
// Usage:
//   const blob = await exportProject(project, { onProgress: p => ... });
//   downloadBlob(blob, "consensus.mp4");

import { renderFrame } from "./videoRender.js";

const DEFAULT_FPS = 30;
const FFMPEG_CORE_VERSION = "0.12.6";
const FFMPEG_CORE_BASE = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/esm`;

let ffmpegLoadPromise = null;

export async function exportProject(
  project,
  { fps = DEFAULT_FPS, onProgress } = {}
) {
  const { width = 1280, height = 720, scenes = [] } = project;
  if (!scenes.length) throw new Error("Project has no scenes.");

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { alpha: false });

  const sceneAssets = await Promise.all(
    scenes.map(async (s) => ({
      scene: s,
      bg: await loadBackground(s.background),
      audio: s.audio?.src ? await loadAudio(s.audio.src) : null,
    }))
  );

  const totalMs = sceneAssets.reduce((acc, s) => acc + s.scene.durationMs, 0);
  const totalFrames = Math.max(1, Math.ceil((totalMs / 1000) * fps));
  const durationSec = totalMs / 1000;

  const [{ fetchFile }] = await Promise.all([import("@ffmpeg/util")]);
  const ffmpeg = await getFfmpeg();
  const frameNames = [];

  try {
    for (let frame = 0; frame < totalFrames; frame++) {
      const globalTime = frame / fps;
      const { item, sceneTime } = locateScene(sceneAssets, globalTime);
      const { scene, bg } = item;

      await syncBackground(bg, sceneTime);
      renderFrame(ctx, {
        width,
        height,
        time: sceneTime,
        duration: scene.durationMs / 1000,
        background: bg,
        captions: scene.captions,
      });

      const name = `frame_${String(frame).padStart(6, "0")}.jpg`;
      await ffmpeg.writeFile(name, await fetchFile(await canvasToJpeg(canvas)));
      frameNames.push(name);

      onProgress?.(((frame + 1) / totalFrames) * 0.72);
    }

    onProgress?.(0.74);
    const audioWav = await renderProjectAudio(sceneAssets, totalMs);
    if (audioWav) {
      await ffmpeg.writeFile("audio.wav", await fetchFile(audioWav));
    }

    onProgress?.(0.76);
    const progressHandler = ({ progress }) => {
      if (typeof progress === "number" && !Number.isNaN(progress)) {
        onProgress?.(0.76 + Math.min(1, Math.max(0, progress)) * 0.24);
      }
    };
    ffmpeg.on("progress", progressHandler);

    const args = [
      "-framerate",
      String(fps),
      "-i",
      "frame_%06d.jpg",
    ];
    if (audioWav) {
      args.push("-i", "audio.wav", "-c:a", "aac");
    } else {
      args.push("-an");
    }
    args.push(
      "-c:v",
      "libx264",
      "-preset",
      "ultrafast",
      "-crf",
      "23",
      "-pix_fmt",
      "yuv420p",
      "-t",
      String(durationSec),
      "-movflags",
      "+faststart",
      "output.mp4"
    );

    try {
      await ffmpeg.exec(args);
    } finally {
      ffmpeg.off("progress", progressHandler);
    }

    const data = await ffmpeg.readFile("output.mp4");
    onProgress?.(1);
    return new Blob([data.buffer], { type: "video/mp4" });
  } finally {
    await Promise.all(
      [...frameNames, "audio.wav", "output.mp4"].map((name) =>
        ffmpeg.deleteFile(name).catch(() => {})
      )
    );
  }
}

function locateScene(sceneAssets, timeSec) {
  let offset = 0;
  for (const item of sceneAssets) {
    const dur = item.scene.durationMs / 1000;
    if (timeSec < offset + dur || item === sceneAssets[sceneAssets.length - 1]) {
      return {
        item,
        sceneTime: Math.max(0, Math.min(timeSec - offset, dur)),
      };
    }
    offset += dur;
  }
  const last = sceneAssets[sceneAssets.length - 1];
  return { item: last, sceneTime: last.scene.durationMs / 1000 };
}

function canvasToJpeg(canvas, quality = 0.92) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Failed to encode video frame.")),
      "image/jpeg",
      quality
    );
  });
}

async function syncBackground(bg, timeSec) {
  if (bg?.type !== "video" || !bg.video) return;
  const video = bg.video;
  const duration = Number.isFinite(video.duration) ? video.duration : timeSec;
  const target = Math.max(0, Math.min(timeSec, duration || timeSec));
  if (Math.abs(video.currentTime - target) <= 0.01) return;

  await new Promise((resolve) => {
    const finish = () => resolve();
    video.addEventListener("seeked", finish, { once: true });
    try {
      video.currentTime = target;
    } catch {
      finish();
    }
  });
}

async function renderProjectAudio(sceneAssets, totalMs) {
  const hasAudio = sceneAssets.some((item) => item.audio);
  if (!hasAudio) return null;

  const sampleRate = 48000;
  const frameCount = Math.ceil((totalMs / 1000) * sampleRate);
  const offline = new OfflineAudioContext(2, frameCount, sampleRate);

  let offsetSec = 0;
  for (const { scene, audio } of sceneAssets) {
    if (audio) {
      const source = offline.createBufferSource();
      source.buffer = audio;
      source.connect(offline.destination);
      source.start(offsetSec);
    }
    offsetSec += scene.durationMs / 1000;
  }

  const rendered = await offline.startRendering();
  return audioBufferToWav(rendered);
}

function audioBufferToWav(buffer) {
  const channels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const samples = buffer.length;
  const bytesPerSample = 2;
  const blockAlign = channels * bytesPerSample;
  const dataSize = samples * blockAlign;
  const headerSize = 44;
  const out = new ArrayBuffer(headerSize + dataSize);
  const view = new DataView(out);

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, dataSize, true);

  const channelData = [];
  for (let c = 0; c < channels; c++) {
    channelData.push(buffer.getChannelData(c));
  }

  let offset = headerSize;
  for (let i = 0; i < samples; i++) {
    for (let c = 0; c < channels; c++) {
      const sample = Math.max(-1, Math.min(1, channelData[c][i] || 0));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([out], { type: "audio/wav" });
}

function writeAscii(view, offset, text) {
  for (let i = 0; i < text.length; i++) {
    view.setUint8(offset + i, text.charCodeAt(i));
  }
}

async function getFfmpeg() {
  if (!ffmpegLoadPromise) {
    ffmpegLoadPromise = (async () => {
      const [{ FFmpeg }, { toBlobURL }] = await Promise.all([
        import("@ffmpeg/ffmpeg"),
        import("@ffmpeg/util"),
      ]);
      const ffmpeg = new FFmpeg();
      await ffmpeg.load({
        coreURL: await toBlobURL(
          `${FFMPEG_CORE_BASE}/ffmpeg-core.js`,
          "text/javascript"
        ),
        wasmURL: await toBlobURL(
          `${FFMPEG_CORE_BASE}/ffmpeg-core.wasm`,
          "application/wasm"
        ),
      });
      return ffmpeg;
    })();
  }
  return ffmpegLoadPromise;
}

async function loadBackground(bg) {
  if (!bg) return { type: "color", color: "#000000" };
  if (bg.type === "color") return bg;
  if (bg.type === "image") return { type: "image", img: await loadImage(bg.src) };
  if (bg.type === "video") return { type: "video", video: await loadVideo(bg.src) };
  return { type: "color", color: "#000000" };
}

function loadImage(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

function loadVideo(src) {
  return new Promise((res, rej) => {
    const v = document.createElement("video");
    v.crossOrigin = "anonymous";
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.src = src;
    v.onloadedmetadata = () => {
      v.pause();
      v.currentTime = 0;
      res(v);
    };
    v.onerror = rej;
  });
}

async function loadAudio(src) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const r = await fetch(src);
  const buf = await r.arrayBuffer();
  return ctx.decodeAudioData(buf);
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}
